









































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import AgentProductMixin from "@/mixins/http/Agents/Product";
import { ClientAgentProduct } from "@/models";
import AdcontrolProfile from "@/models/AdcontrolProfile";
import EntryListGroupVariant from "./EntryListGroupVariant.vue";

const CONFIG = {
  DEFAULT_PLACEHOLDER: "Cerca prodotti",
  MIN_SEARCH_LENGTH: 1,
  DEBOUNCE_DELAY: 500,
} as const;

interface SearchResults {
  clientProducts: ClientAgentProduct[];
  total: number;
}

@Component({
  components: { EntryListGroupVariant },
})
export default class ClientProductSearch extends Mixins(AgentProductMixin) {
  @Prop({ default: CONFIG.DEFAULT_PLACEHOLDER })
  readonly placeholder!: string;

  @Prop({ default: CONFIG.MIN_SEARCH_LENGTH })
  readonly min!: number;

  @Prop({ default: CONFIG.DEBOUNCE_DELAY })
  readonly delay!: number;

  @Prop({ default: false })
  readonly selectable!: boolean;

  @Prop({ default: "right" })
  readonly iconAlign!: string;

  @Prop()
  readonly size?: string;

  @Prop({ default: "" })
  readonly dropdownSize!: string;

  @Prop({ default: "" })
  readonly dropdownAlign!: string;

  @Prop()
  readonly label?: string;

  @Prop()
  readonly company?: string;

  @Getter("profile")
  readonly profile!: AdcontrolProfile;

  private searchTerm = "";
  private products: ClientAgentProduct[] = [];
  private isSearching = false;
  private isDropdownVisible = false;
  private isInitialLoadComplete = false;
  private lastSearchTerm = "";
  private debounceTimer?: number;

  get searchClasses(): string {
    return `entry-search--${this.iconAlign}`;
  }

  get dropdownClasses(): string[] {
    return [this.dropdownSize, this.dropdownAlign];
  }

  get searchResults(): SearchResults {
    return {
      clientProducts: this.products,
      total: this.products.length,
    };
  }

  get hasResults(): boolean {
    return this.products.length > 0;
  }

  @Watch("label", { immediate: true })
  onLabelChange(newLabel?: string): void {
    this.searchTerm = newLabel ?? "";
  }

  public focus(): void {
    const input = this.$refs.inputRef as HTMLInputElement;
    input?.focus();
  }

  public clearSearch(): void {
    this.resetSearchState();
    this.$emit("search-cleared");
  }

  async handleFocus(): Promise<void> {
    if (!this.isInitialLoadComplete) {
      await this.loadInitialProducts();
    }
    this.openDropdown();
  }

  handleKeyUp(): void {
    if (this.shouldSearch()) {
      this.debouncedSearch();
    } else if (this.isSearchEmpty()) {
      this.loadInitialProducts();
    }
  }

  handleSelection(product: ClientAgentProduct): void {
    this.searchTerm = product.name;
    this.$emit("selected", product.clone());
    this.closeDropdown();
  }

  handleClickOutside(): void {
    this.closeDropdown();
  }

  private isLoading(): string {
    return this.isSearching ? "spinner" : "search";
  }

  private async loadInitialProducts(): Promise<void> {
    await this.performSearch("");
    this.isInitialLoadComplete = true;
  }

  private debouncedSearch(): void {
    this.clearDebounceTimer();
    this.debounceTimer = setTimeout(() => {
      this.search();
    }, this.delay);
  }

  private async search(): Promise<void> {
    const query = this.sanitizeSearchTerm();

    if (this.isDuplicateSearch(query)) return;
    this.lastSearchTerm = query;
    await this.performSearch(query);

    if (this.selectable) {
      this.openDropdown();
    }
  }

  private async performSearch(term: string): Promise<void> {
    this.setSearchingState(true);

    try {
      this.products = await this.fetchProducts(term);
    } finally {
      this.setSearchingState(false);
    }

    this.$emit("searched", this.searchResults);
  }

  private async fetchProducts(term: string): Promise<ClientAgentProduct[]> {
    const params: any = {
      "filter[term]": term,
      "filter[principals]": 1,
      "filter[principal_company]": this.company,
    };

    return await this.getProducts(this.profile.slug, params);
  }

  private shouldSearch(): boolean {
    return this.searchTerm.length >= this.min;
  }

  private isSearchEmpty(): boolean {
    return this.searchTerm.length === 0;
  }

  private sanitizeSearchTerm(): string {
    return this.searchTerm.trim();
  }

  private isDuplicateSearch(query: string): boolean {
    return query === this.lastSearchTerm;
  }

  private setSearchingState(searching: boolean): void {
    this.isSearching = searching;

    if (searching) {
      this.$emit("searching");
    }

    this.clearDebounceTimer();
  }

  private clearDebounceTimer(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = undefined;
    }
  }

  private resetSearchState(): void {
    this.products = [];
    this.searchTerm = "";
    this.isInitialLoadComplete = false;
    this.lastSearchTerm = "";
  }

  private openDropdown(): void {
    this.isDropdownVisible = true;
  }

  private closeDropdown(): void {
    this.isDropdownVisible = false;
  }
}
